<template>
<div>
<!-- Modals -->
<share-modal
    v-if="!no_shares && (modals.share.entities_to_share || modals.share.edit_share)"
    :user="user"
    :entities="modals.share.entities_to_share"
    :editShare="modals.share.edit_share"
    @close="resetModals()"
/>

<file-viewer-modal 
    v-if="!no_file_viewer && modals.file_viewer.file"
    :file="modals.file_viewer.file"
    :viewable_file_list="modals.file_viewer.viewable_file_list"
    @close="resetModals()"
/>

<copy-move-modal 
    v-if="!no_copy_move && modals.copy_move.file"
    :targetFile="modals.copy_move.file"
    :action="modals.copy_move.action"
    :is_user_admin="user?.is_admin"
    :is_client_space_client="user?.is_client_space_client"
    @close="resetModals()"
/>
    
<file-info-modal
    v-if="!no_file_info && modals.info.file"
    :file="modals.info.file"
    :view="modals.info.view"
    :user="user"
    @close="resetModals()"
/>
</div>
</template>
<script lang="js">

import shareModal from '@/components/modals/share.vue'
import fileViewerModal from '@/components/modals/file-viewer.vue'
import copyMoveModal from '@/components/modals/copy-move.vue'
import fileInfoModal from '@/components/modals/file-info.vue'

import { bus, events } from '@/helpers/event_bus'
import { Utils } from '@/helpers/utils'

import debounce from 'lodash/debounce';


/**
 * Sets up modal dialogs and the DOWNLOAD_FILE event handler. 
 * Modals can be opened and provide the results of their operations via the event bus.
 * You can control which modals/events are enabled with the boolean props, which are reactive
 * and dynamically activate/disable the available functionality when the props change.
 * 
 * - Create/edit Share modal: disable with `no_shares`
 * - File Preview modal: disable with `no_file_viewer`
 * - Copy/move modal (namespace tree): disable with `no_copy_move`
 * - File info modal (basic info, versions, shares): disable with `no_file_info`
 * - File download event handler: disable with `no_download`
 */
export default {
    components: {
        shareModal,
        fileViewerModal,
        copyMoveModal,
        fileInfoModal
    },

    props: {
        user: [Object, null],
        no_shares: {type: Boolean, default: false},
        no_file_viewer: {type: Boolean, default: false},
        no_copy_move: {type: Boolean, default: false},
        no_file_info: {type: Boolean, default: false},
        no_download: {type: Boolean, default: false},
    },

    data(){
        return {
            modals: this.getDefaultModalState(),
            changedProps: new Set()
        }
    },

    created() {
        // Register or de-register event listeners when any boolean prop changes.
        Object.keys(this.$options.props)
            .filter(p => this.$options.props[p] === Boolean || this.$options.props[p].type === Boolean)
            .forEach(prop => {
                this.$watch(prop, () => {
                    this.changedProps.add(prop);
                    // Debounce when multiple props change (almost) at the same time
                    this.debouncedOnPropsChanged();
                });
            });
    },

    mounted(){
        this.setupEventListeners()
    },

    methods: {

        getDefaultModalState() {
            return {
                share: {
                    entities_to_share: null,
                    edit_share: null
                },
                file_viewer: {
                    file: null,
                    version_id: null,
                    viewable_file_list: null
                },
                copy_move: {
                    action: null,
                    file: null
                },

                info: {
                    file: null,
                    page: null
                }
            }
        },

        resetModals() {
            this.modals = this.getDefaultModalState(); // Replace with fresh copy
        },

        setupEventListeners(){
            // Register or de-register event listeners based on the boolean props
            if(!this.no_shares){
                bus.$on(events.CREATE_SHARE, (entities_to_share) => {
                    this.modals.share.entities_to_share = entities_to_share
                })
                bus.$on(events.EDIT_SHARE, (share) => {
                    this.modals.share.edit_share = share
                })
            }
            else{
                bus.$off(events.CREATE_SHARE)
                bus.$off(events.EDIT_SHARE)
            }

            if(!this.no_file_viewer){
                bus.$on(events.OPEN_FILE_VIEWER, ({file, previewable_files}) => {
                    this.modals.file_viewer.file = file
                    this.modals.file_viewer.viewable_file_list = previewable_files
                })
            }
            else{
                bus.$off(events.OPEN_FILE_VIEWER)
            }

            if(!this.no_copy_move){
                bus.$on(events.OPEN_COPY_MOVE, ({action, file}) => {
                    this.modals.copy_move.action = action
                    this.modals.copy_move.file = file
                })
            }
            else{
                bus.$off(events.OPEN_COPY_MOVE)
            }
            
            if(!this.no_file_info){
                bus.$on(events.OPEN_INFO_MODAL, ({file, view}) => {
                    this.modals.info.file = file
                    this.modals.info.view = view
                })
            }
            else{
                bus.$off(events.OPEN_INFO_MODAL)
            }
            
            if(!this.no_download){
                bus.$on(events.DOWNLOAD_FILE, ({file, version_id}) => {
                    Utils.download_or_open_file(file, version_id)
                })
            }
            else{
                bus.$off(events.DOWNLOAD_FILE)
            }
        },

        // Debounce handler that batches prop changes (e.g., if multiple props change within 100ms, setupEventListeners is only called once)
        debouncedOnPropsChanged: debounce(function () {
            this.changedProps.clear();
            this.setupEventListeners();
        }, 100) // debounce delay (ms)
    }
}


</script>